import apiClient from './apiClient';
import Cookies from 'js-cookie';
import logger from '../utils/logger';

const API_ENDPOINTS = {
  GET_PLANS: '/api/subscription/Plans',
  GET_USER_SUBSCRIPTION: '/api/subscription/UserSubscription',
  CHECKOUT_SUBSCRIPTION: '/api/subscription/CheckOut',
  CANCEL_SUBSCRIPTION: '/api/subscription/CancelSubscriptionPlan',
  ACTIVATE_SUBSCRIPTION: '/api/subscription/SubscriptionPlan',
  REDEEM_CODE: '/api/subscription/RedeemCode',
};

/**
 * Obține lista abonamentelor disponibile
 * @returns {Promise<Object>} - Răspunsul de la server
 */
export const getPlans = async () => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.GET_PLANS);

    return response.data;
  } catch (error) {
    logger.error('Eroare la obținerea planurilor de abonament:', error);
    throw new Error(
      error.response?.data?.message ||
        'A apărut o eroare la preluarea abonamentelor.',
    );
  }
};

/**
 * Obține lista abonamentelor utilizatorului
 * @returns {Promise<Array>} - Lista abonamentelor utilizatorului
 */
export const getUserSubscriptions = async () => {
  try {
    const userId = Cookies.get('userId');
    if (!userId) throw new Error('User ID is missing');

    const response = await apiClient.get(API_ENDPOINTS.GET_USER_SUBSCRIPTION, {
      params: { userId },
    });

    return response.data;
  } catch (error) {
    logger.error('Eroare la obținerea abonamentelor utilizatorului:', error);
    throw new Error(
      error.response?.data?.message ||
        'A apărut o eroare la preluarea abonamentelor.',
    );
  }
};

/**
 * Anulează abonamentul unui utilizator
 * @returns {Promise<void>}
 */
export const cancelSubscription = async (userId, token) => {
  try {
    if (!userId) throw new Error('User ID is missing');
    if (!token) throw new Error('JWT Token is missing');

    const response = await apiClient.put(
      `${API_ENDPOINTS.CANCEL_SUBSCRIPTION}/${userId}`,
      {},
    );

    logger.log('Cancel subscription response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Eroare la anularea abonamentului:', error);
    logger.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url,
    });

    throw new Error(
      error.response?.data?.message ||
        error.response?.data ||
        'A apărut o eroare la anularea abonamentului.',
    );
  }
};

/**
 * Inițiază procesul de checkout pentru abonament
 * @param {string} userId - ID-ul utilizatorului
 * @param {string} planId - ID-ul planului
 * @returns {Promise<Object>} - URL-ul de checkout
 */
export const checkoutSubscription = async (userId, planId) => {
  try {
    const response = await apiClient.post(
      `${API_ENDPOINTS.CHECKOUT_SUBSCRIPTION}/${userId}/${planId}`,
      {},
    );
    return response.data;
  } catch (error) {
    logger.error('Eroare la inițierea checkout-ului abonamentului:', error);
    throw new Error(
      error.response?.data?.message ||
        'A apărut o eroare la inițierea checkout-ului.',
    );
  }
};

/**
 * Confirmă dacă abonamentul a fost plătit cu succes
 * @param {string} paymentIntentId - ID-ul intenției de plată
 * @returns {Promise<Object>} - Rezultatul confirmării
 */
export const confirmSubscriptionPayment = async (paymentIntentId) => {
  try {
    const response = await apiClient.get(
      `${API_ENDPOINTS.CONFIRM_PAYMENT}/${paymentIntentId}`,
    );
    return response.data;
  } catch (error) {
    logger.error('Eroare la confirmarea plății abonamentului:', error);
    throw new Error(
      error.response?.data?.message ||
        'A apărut o eroare la confirmarea plății abonamentului.',
    );
  }
};

/**
 * Activează abonamentul după succesul plății
 * @param {string} userId - ID-ul utilizatorului
 * @param {string} sessionId - ID-ul sesiunii Stripe
 * @param {string} subscriptionId - ID-ul abonamentului
 * @returns {Promise<Object>} - Răspunsul serverului
 */
/**
 * Activeaza un cod promotional
 * @param {string} code - Codul promotional
 */
export const redeemCode = async (code) => {
  try {
    const response = await apiClient.post(API_ENDPOINTS.REDEEM_CODE, { code });
    return response.data;
  } catch (error) {
    logger.error('Eroare la activarea codului promotional:', error);
    throw new Error(
      error.response?.data?.message ||
        'A apărut o eroare la activarea codului.',
    );
  }
};

export const activateSubscription = async (
  userId,
  sessionId,
  subscriptionId,
) => {
  try {
    const token = Cookies.get('jwtToken');
    const url = `${API_ENDPOINTS.ACTIVATE_SUBSCRIPTION}/${userId}/${sessionId}/${subscriptionId}`;

    logger.log('Activating subscription with:', {
      userId,
      sessionId,
      subscriptionId,
      token: token ? 'present' : 'missing',
      url,
    });

    const response = await apiClient.post(url, {});

    logger.log('Activation response:', response.data);
    return response.data;
  } catch (error) {
    logger.error('Eroare la activarea abonamentului:', error);
    logger.error('Error details:', {
      message: error.message,
      response: error.response?.data,
      status: error.response?.status,
      url: error.config?.url,
    });

    throw new Error(
      error.response?.data?.message ||
        'A apărut o eroare la activarea abonamentului.',
    );
  }
};
